import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = async (event) => {
    try {
      const sections = event?.detail?.data?.sections;
      const cartItems = this.querySelector('cart-items-component');
      const sectionId = cartItems?.dataset.sectionId;

      if (sections && sectionId && sections[sectionId]) {
        const doc = new DOMParser().parseFromString(sections[sectionId], 'text/html');
        const newCartItems = doc.querySelector('cart-items-component');

        if (newCartItems && cartItems) {
          morph(cartItems, newCartItems);
        }
      } else {
        await this.refreshCartDrawer();
      }
    } catch (error) {
      console.error('Cart drawer update failed:', error);
      await this.refreshCartDrawer();
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  async refreshCartDrawer() {
    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems?.dataset.sectionId;

    if (!sectionId) {
      console.warn('Cart drawer refresh failed: no sectionId found.');
      return;
    }

    const response = await fetch(`/?sections=${sectionId}`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart drawer section: ${response.status}`);
    }

    const sections = await response.json();
    const html = sections[sectionId];

    if (!html) {
      console.warn('Cart drawer refresh failed: no HTML returned for section.');
      return;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newCartItems = doc.querySelector('cart-items-component');

    if (newCartItems && cartItems) {
      morph(cartItems, newCartItems);
    }
  }

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}